import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Users, 
  Search, 
  Edit2, 
  Trash2, 
  Plus, 
  Filter, 
  UserPlus, 
  MoreVertical,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function EmployesManagement({ onNavigate }) {
  const { hasPermission } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [postes, setPostes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [empRes, deptRes, posteRes] = await Promise.all([
        axios.get(`${API}/rh/employes`, { headers: headers() }),
        axios.get(`${API}/rh/departements`, { headers: headers() }),
        axios.get(`${API}/rh/postes`, { headers: headers() })
      ]);
      setEmployees(empRes.data);
      setDepartements(deptRes.data);
      setPostes(posteRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => 
      `${emp.prenom} ${emp.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.poste?.nom?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employees, searchTerm]);

  const paginatedEmployees = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredEmployees.slice(start, start + itemsPerPage);
  }, [filteredEmployees, currentPage]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const handleAddEmployee = () => {
    Swal.fire({
      title: 'Nouvel Employé',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Prénom *</label>
            <input id="swal-prenom" class="rh-modal-input" placeholder="Prénom">
          </div>
          <div class="rh-input-group">
            <label>Nom *</label>
            <input id="swal-nom" class="rh-modal-input" placeholder="Nom">
          </div>
          <div class="rh-input-group">
            <label>CIN *</label>
            <input id="swal-cin" class="rh-modal-input" placeholder="CIN">
          </div>
          <div class="rh-input-group">
            <label>Département *</label>
            <select id="swal-dept" class="rh-modal-input">
              <option value="">Sélectionner...</option>
              ${departements.map(d => `<option value="${d._id}">${d.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-input-group">
            <label>Poste *</label>
            <select id="swal-poste" class="rh-modal-input">
              <option value="">Sélectionner...</option>
              ${postes.map(p => `<option value="${p._id}">${p.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-input-group">
            <label>Date début contrat *</label>
            <input id="swal-date" type="date" class="rh-modal-input" value="${new Date().toISOString().split('T')[0]}">
          </div>
          <div class="rh-input-group">
            <label>Téléphone *</label>
            <input id="swal-phone" class="rh-modal-input" placeholder="98 000 000">
          </div>
          <div class="rh-input-group">
            <label>Email</label>
            <input id="swal-email" class="rh-modal-input" placeholder="email@exemple.com">
          </div>
          <div class="rh-input-group">
            <label>Type de Contrat *</label>
            <select id="swal-typeContrat" class="rh-modal-input">
              <option value="CDI">CDI</option>
              <option value="CDD">CDD</option>
              <option value="SIVP">SIVP</option>
              <option value="Karama">Karama</option>
              <option value="Stage">Stage</option>
              <option value="Freelance">Freelance / Sous-traitant</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Ajouter l\'employé',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        const prenom = document.getElementById('swal-prenom').value;
        const nom = document.getElementById('swal-nom').value;
        const cin = document.getElementById('swal-cin').value;
        const departement = document.getElementById('swal-dept').value;
        const poste = document.getElementById('swal-poste').value;
        const dateDebutContrat = document.getElementById('swal-date').value;
        const telephone = document.getElementById('swal-phone').value;
        const email = document.getElementById('swal-email').value;
        const typeContrat = document.getElementById('swal-typeContrat').value;
        
        if (!prenom || !nom || !cin || !departement || !poste || !dateDebutContrat || !telephone) {
          Swal.showValidationMessage('Veuillez remplir tous les champs obligatoires (*)');
          return false;
        }
        return { prenom, nom, cin, departement, poste, dateDebutContrat, telephone, email, typeContrat };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(`${API}/rh/employes`, result.value, { headers: headers() });
          Swal.fire({ icon: 'success', title: 'Employé ajouté !', confirmButtonColor: '#0F2343' });
          fetchData();
        } catch (err) {
          Swal.fire('Erreur', err.response?.data?.message || 'Impossible d\'ajouter l\'employé.', 'error');
        }
      }
    });
  };

  const handleEditEmployee = (emp) => {
    Swal.fire({
      title: 'Modifier Employé',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Prénom *</label>
            <input id="swal-prenom" class="rh-modal-input" value="${emp.prenom}">
          </div>
          <div class="rh-input-group">
            <label>Nom *</label>
            <input id="swal-nom" class="rh-modal-input" value="${emp.nom}">
          </div>
          <div class="rh-input-group">
            <label>Département *</label>
            <select id="swal-dept" class="rh-modal-input">
              ${departements.map(d => `<option value="${d._id}" ${d._id === emp.departement?._id ? 'selected' : ''}>${d.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-input-group">
            <label>Poste *</label>
            <select id="swal-poste" class="rh-modal-input">
              ${postes.map(p => `<option value="${p._id}" ${p._id === emp.poste?._id ? 'selected' : ''}>${p.nom}</option>`).join('')}
            </select>
          </div>
          <div class="rh-input-group">
            <label>Téléphone *</label>
            <input id="swal-phone" class="rh-modal-input" value="${emp.telephone}">
          </div>
          <div class="rh-input-group">
            <label>Statut</label>
            <select id="swal-statut" class="rh-modal-input">
              <option value="actif" ${emp.statut === 'actif' ? 'selected' : ''}>Actif</option>
              <option value="conge" ${emp.statut === 'conge' ? 'selected' : ''}>En Congé</option>
              <option value="suspendu" ${emp.statut === 'suspendu' ? 'selected' : ''}>Suspendu</option>
            </select>
          </div>
          <div class="rh-input-group">
            <label>Type de Contrat</label>
            <select id="swal-typeContrat" class="rh-modal-input">
              <option value="CDI" ${emp.typeContrat === 'CDI' ? 'selected' : ''}>CDI</option>
              <option value="CDD" ${emp.typeContrat === 'CDD' ? 'selected' : ''}>CDD</option>
              <option value="SIVP" ${emp.typeContrat === 'SIVP' ? 'selected' : ''}>SIVP</option>
              <option value="Karama" ${emp.typeContrat === 'Karama' ? 'selected' : ''}>Karama</option>
              <option value="Stage" ${emp.typeContrat === 'Stage' ? 'selected' : ''}>Stage</option>
              <option value="Freelance" ${emp.typeContrat === 'Freelance' ? 'selected' : ''}>Freelance / Sous-traitant</option>
              <option value="Autre" ${emp.typeContrat === 'Autre' ? 'selected' : ''}>Autre</option>
            </select>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        return {
          prenom: document.getElementById('swal-prenom').value,
          nom: document.getElementById('swal-nom').value,
          departement: document.getElementById('swal-dept').value,
          poste: document.getElementById('swal-poste').value,
          telephone: document.getElementById('swal-phone').value,
          statut: document.getElementById('swal-statut').value,
          typeContrat: document.getElementById('swal-typeContrat').value
        };
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.put(`${API}/rh/employes/${emp._id}`, result.value, { headers: headers() });
          Swal.fire('Modifié !', '', 'success');
          fetchData();
        } catch (err) {
          Swal.fire('Erreur', 'Impossible de modifier l\'employé.', 'error');
        }
      }
    });
  };

  const handleDeleteEmployee = (id, name) => {
    Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: `Vous allez supprimer ${name}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E53E3E',
      confirmButtonText: 'Oui, supprimer'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API}/rh/employes/${id}`, { headers: headers() });
          Swal.fire('Supprimé !', '', 'success');
          fetchData();
        } catch (err) {
          Swal.fire('Erreur', 'Impossible de supprimer l\'employé.', 'error');
        }
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-table-card">
        <div className="rh-table-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="rh-hero-icon" style={{ width: '45px', height: '45px', borderRadius: '12px' }}>
              <Users size={24} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Liste des Employés</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)' }}>{employees.length} collaborateurs au total</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="rh-search-wrapper" style={{ width: '300px' }}>
              <Search className="rh-icon-search" size={16} />
              <input 
                type="text" 
                className="rh-search-input" 
                placeholder="Rechercher..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="rh-add-btn" onClick={handleAddEmployee}>
              <UserPlus size={18} /> Ajouter
            </button>
          </div>
        </div>

        <div className="rh-table-container">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Collaborateur</th>
                <th>Poste & Dept</th>
                <th>Contact</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEmployees.map(emp => (
                <tr key={emp._id}>
                  <td>
                    <div className="rh-user-cell" onClick={() => onNavigate('rh-employe-detail', emp._id)} style={{ cursor: 'pointer' }}>
                      <div className="rh-employee-avatar-small" style={{ background: 'var(--rh-blue-gradient)' }}>
                        {(emp.prenom?.[0] || '') + (emp.nom?.[0] || '')}
                      </div>
                      <div className="rh-user-info">
                        <span className="rh-user-name">{emp.prenom} {emp.nom}</span>
                        <span className="rh-user-role">CIN: {emp.cin || '—'}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="rh-user-info">
                      <span className="rh-user-name" style={{ fontSize: '14px' }}>{emp.poste?.nom || '—'}</span>
                      <span className="rh-user-role">{emp.departement?.nom || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                        <Phone size={12} color="var(--rh-text-muted)" /> {emp.telephone}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--rh-text-muted)' }}>
                        <Mail size={12} /> {emp.email || '—'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`rh-status-badge ${emp.statut === 'actif' ? 'approved' : emp.statut === 'conge' ? 'info' : 'rejected'}`}>
                      {emp.statut}
                    </span>
                  </td>
                  <td>
                    <div className="rh-actions" style={{ justifyContent: 'flex-end' }}>
                      <button className="rh-action-btn edit" onClick={() => handleEditEmployee(emp)}>
                        <Edit2 size={16} />
                      </button>
                      <button className="rh-action-btn delete" onClick={() => handleDeleteEmployee(emp._id, `${emp.prenom} ${emp.nom}`)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="rh-pagination">
            <span className="rh-pagination-info">Page {currentPage} sur {totalPages}</span>
            <div className="rh-pagination-btns">
              <button 
                className="rh-page-btn" 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button 
                  key={i} 
                  className={`rh-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="rh-page-btn" 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
